import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/integrated-auth';
import { useChatContext } from '@/contexts/chat-context';
import { apiRequest } from '@/lib/queryClient';
import { MessageSquare, Trophy, MapPin, Loader2 } from 'lucide-react';

interface ChatLeague {
  id: number;
  name: string;
  themeColor?: string;
  headerImageUrl?: string | null;
  memberCount?: number;
  participantCount?: number;
  isOwner?: boolean;
  chatEnabled?: boolean;
  chatImagesAllowed?: boolean;
}

interface CommunityChannel {
  id: number;
  name: string;
  description: string | null;
  type: 'category' | 'general';
  categoryId: number | null;
  emoji: string | null;
  memberCount: number;
  category?: {
    id: number;
    name: string;
    type: string | null;
  } | null;
}

interface MyScene {
  id: number;
  name: string;
  type: string;
}

export function ChatHub() {
  const { isAuthenticated } = useAuth();
  const { setChatTarget, chatNotifications } = useChatContext();

  // Fetch user's active leagues
  const { data: leagues, isLoading: leaguesLoading } = useQuery<ChatLeague[]>({
    queryKey: ['/api/leagues/user/active'],
    enabled: isAuthenticated,
    staleTime: 60000,
  });

  // Fetch user's scenes
  const { data: myScenes, isLoading: scenesLoading } = useQuery<MyScene[]>({
    queryKey: ['/api/scenes/my'],
    queryFn: () => apiRequest('/api/scenes/my'),
    enabled: isAuthenticated,
    staleTime: 30000,
  });

  const { data: joinedChannels, isLoading: communityLoading } = useQuery<CommunityChannel[]>({
    queryKey: ['/api/community/channels/my'],
    queryFn: () => apiRequest('/api/community/channels/my', 'GET'),
    enabled: isAuthenticated,
    staleTime: 30000,
  });

  const isLoading = leaguesLoading || scenesLoading || communityLoading;

  // Filter leagues that have chat enabled
  const chatLeagues = (leagues || []).filter((league) => league.chatEnabled !== false);

  // Build a lookup map for unread counts and last message timestamps
  const unreadMap = new Map(chatNotifications.map(n => [n.leagueId, n]));

  // Sort by: most recent message first, then alphabetically for leagues with no messages
  const sortedLeagues = [...chatLeagues].sort((a, b) => {
    const aNotif = unreadMap.get(a.id);
    const bNotif = unreadMap.get(b.id);
    const aTime = aNotif?.lastMessageAt?.getTime() ?? 0;
    const bTime = bNotif?.lastMessageAt?.getTime() ?? 0;
    if (bTime !== aTime) return bTime - aTime;
    return (a.name || '').localeCompare(b.name || '');
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const hasNoChats = chatLeagues.length === 0
    && (!myScenes || myScenes.length === 0)
    && (!joinedChannels || joinedChannels.length === 0);

  if (hasNoChats) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-center p-6">
        <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No Chats Yet</h3>
        <p className="text-muted-foreground">Join a league or scene to start chatting!</p>
      </div>
    );
  }

  return (
    <div className="overflow-y-auto h-full">
      {/* League chats */}
      {sortedLeagues.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
            Leagues
          </div>
          {sortedLeagues.map((league) => {
            const notif = unreadMap.get(league.id);
            const unreadCount = notif?.unreadCount ?? 0;
            return (
              <button
                key={league.id}
                onClick={() => {
                  setChatTarget({
                    type: 'league',
                    leagueId: league.id,
                    leagueName: league.name,
                    isOwner: league.isOwner ?? false,
                    chatEnabled: league.chatEnabled ?? true,
                    chatImagesAllowed: league.chatImagesAllowed ?? true,
                  });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 active:bg-muted transition-colors text-left"
              >
                <div className="relative">
                  {league.headerImageUrl ? (
                    <img
                      src={league.headerImageUrl}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: league.themeColor || '#7c3aed' }}
                    >
                      <Trophy className="h-5 w-5 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm truncate ${unreadCount > 0 ? 'font-semibold' : ''}`}>{league.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {notif?.lastMessagePreview
                      ? <span className={unreadCount > 0 ? 'text-foreground/70' : ''}>{notif.lastMessageAuthor ? `${notif.lastMessageAuthor}: ` : ''}{notif.lastMessagePreview}</span>
                      : `${league.memberCount || league.participantCount || 0} members`
                    }
                  </p>
                </div>
                {unreadCount > 0 ? (
                  <span className="min-w-[22px] h-[22px] rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center px-1 shrink-0">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                ) : (
                  <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* Joined community rooms */}
      {joinedChannels && joinedChannels.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
            Rooms
          </div>
          {joinedChannels.map((channel) => {
            const subtitle = channel.description
              || (channel.type === 'general'
                ? 'General room'
                : `${channel.category?.type === 'music' ? 'Music' : 'Video'} room`);

            return (
              <button
                key={channel.id}
                onClick={() => {
                  setChatTarget({
                    type: 'community',
                    channelId: channel.id,
                    channelName: channel.name,
                    categoryId: channel.categoryId,
                    categoryType: channel.category?.type ?? null,
                    isMember: true,
                    emoji: channel.emoji,
                  });
                }}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 active:bg-muted transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg">
                  {channel.emoji || '💬'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{channel.name}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {subtitle}
                    {channel.memberCount > 0 ? ` · ${channel.memberCount} members` : ''}
                  </p>
                </div>
                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
              </button>
            );
          })}
        </div>
      )}

      {/* Scene chats */}
      {myScenes && myScenes.length > 0 && (
        <div>
          <div className="px-4 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider bg-muted/50">
            Scenes
          </div>
          {myScenes.map((scene) => (
            <button
              key={scene.id}
              onClick={() => {
                setChatTarget({
                  type: 'scene',
                  sceneId: scene.id,
                  sceneName: scene.name,
                  isMember: true,
                });
              }}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 active:bg-muted transition-colors text-left"
            >
              <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <MapPin className="h-5 w-5 text-green-600 dark:text-green-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate">{scene.name}</p>
                <p className="text-xs text-muted-foreground capitalize">{scene.type}</p>
              </div>
              <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
